/* eslint-disable @typescript-eslint/restrict-plus-operands */
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { ThemePalette } from 'mui.theme';
import type { productionAsset } from 'types/production.types';
import { truncateTo2dp } from 'utils/helpers/general';
import type { DeviceStats } from 'types/utilization.types';
import { convertsecondsToHours } from 'views/Utilization/utils';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  data: productionAsset[] | DeviceStats[];
  chartKey:
    | 'avg_load_per_hour'
    | 'max_load_sum'
    | 'avg_cycle_time'
    | 'avg_utilization_by_whole_day'
    | 'total_fuel_consumption_l'
    | 'total_idling_duration'
    | 'down_time_hours';
  keyDisplay: string;
}

export default function Chart({
  enableDarkTheme,
  data,
  chartKey,
  keyDisplay,
}: Props) {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const COLORS = {
    normal: palette.primary,
    highlight: '#EB5757',
  };
  const sortedChartData = [...data].sort(
    (a: productionAsset | DeviceStats, b: productionAsset | DeviceStats) =>
      (b as any)[chartKey] - (a as any)[chartKey]
  );

  const transformedData = sortedChartData.map((item) => {
    if (chartKey === 'total_idling_duration') {
      return {
        ...item,
        [chartKey]: convertsecondsToHours((item as any)[chartKey]),
      };
    }
    if (chartKey === 'down_time_hours') {
      return {
        ...item,
        [chartKey]: (item as any)[chartKey] / (60 * 60 * 60),
      };
    }

    return item;
  });

  const average =
    transformedData.reduce((sum, item) => sum + (item as any)[chartKey], 0) /
    transformedData.length;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={transformedData}
          margin={{ top: 30, right: 90, left: -10, bottom: 0 }}
          barCategoryGap="10%"
        >
          <CartesianGrid
            stroke={palette.cartesianGrid}
            strokeDasharray="2 2"
            vertical={false}
          />
          <XAxis
            dataKey="bumper_id"
            axisLine={false}
            tickLine={false}
            tick={{ fill: ThemePalette.typography.grey, fontSize: 10 }}
            interval={0}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: ThemePalette.typography.grey, fontSize: 10 }}
            label={{
              value: keyDisplay,
              angle: -90,
              position: 'inside',
              fill: '#9CA3AF',
              dy: 10,
              dx: -12,
              fontSize: '10px',
            }}
          />
          <Tooltip
            cursor={{ fill: enableDarkTheme ? '#1B2B4A' : '#CCCCCC' }}
            content={
              <CustomTooltip keyDisplay={keyDisplay} chartKey={chartKey} />
            }
          />

          <ReferenceLine
            y={average}
            stroke={palette.primary}
            strokeDasharray="3 3"
            strokeWidth={1}
            label={{
              value: `Avg: ${truncateTo2dp(average)}`,
              fill: palette.textPrimary,
              fontSize: 14,
              position: 'right',
            }}
          />

          <Bar dataKey={chartKey} radius={[2, 2, 0, 0]} barSize={25}>
            {transformedData.map((entry, index) => {
              const value = (entry as any)[chartKey];
              const color = value < average ? COLORS.highlight : COLORS.normal;
              return <Cell key={`cell-${index}`} fill={color} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const CustomTooltip = ({
  active,
  payload,
  keyDisplay,
  chartKey,
}: {
  active?: boolean;
  payload?: any;
  keyDisplay: string;
  chartKey: string;
}) => {
  if (active && payload && payload.length > 0) {
    const value = truncateTo2dp(payload[0].payload[chartKey]) ?? 'N/A';
    return (
      <div
        style={{
          backgroundColor: 'rgba(217, 229, 247, 0.8) ',
          border: '1px solid #ccc',
          color: '#000',
          padding: '3px 5px',
          borderRadius: '4px',
        }}
      >
        <p>{`${keyDisplay}: ${value}`}</p>
      </div>
    );
  }
  return null;
};
